/**
 * Duel Provider
 *
 * React Context provider for duel state management.
 * Wraps WebSocket service and provides React hooks for components.
 */

import type { Dispatch, SetStateAction } from "react";
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";

import { getDraftType } from "types/duel-draft-type";

import { createDuel } from "@/services/duel/create-duel";
import { dequeuePlayer } from "@/services/duel/dequeue-player";
import { enqueuePlayer } from "@/services/duel/enqueue-player";
import { getPlayerQueueStatus } from "@/services/duel/get-player-queue-status";
import { quitDuel as quitDuelService } from "@/services/duel/quit-duel";
import { DuelWebSocketService } from "@/services/duel/websocket-service";

import type {
  DuelState,
  ServerMessage,
  WebSocketConnectionStatus,
  DuelStepState,
  DuelBattleState,
} from "types/duels/types";

/** Pokemon-like shape for queue screen display (sprites only). */
export type QueuedTeamPokemon = {
  id: number;
  base_pokemon_name: string;
  [key: string]: unknown;
};

interface DuelContextValue {
  // State
  duelState: DuelState;
  setDuelState: Dispatch<SetStateAction<DuelState>>;
  queueStatus: "idle" | "searching" | "matched" | "error";
  /** Team passed when enqueueing; shown on queue screen until duel_initialized. */
  queuedTeam: QueuedTeamPokemon[] | null;
  // Actions
  createAndConnectToDuel: (
    token: string,
    playerId: string,
    opponentPlayerId: string,
    draftType: string,
  ) => Promise<{ success: boolean; error?: string }>;
  enqueue: (
    token: string,
    playerId: string,
    queueId: string,
    teamId: number,
    teamPokemons?: QueuedTeamPokemon[],
  ) => Promise<void>;
  cancelQueue: (token: string) => Promise<void>;
  connectToDuel: (
    duelId: string,
    playerId: string,
    token: string,
    websocketUrl: string,
    isReconnecting?: boolean,
  ) => Promise<void>;
  disconnectFromDuel: () => void;
  submitPick: (pokemonIds: number[]) => void;
  submitBan: (pokemonIds: number[]) => void;
  acknowledgeBattleEnd: () => void;
  quitDuel: (
    token: string,
    duelId?: string,
  ) => Promise<{ success: boolean; error?: string; zombieCleanup?: boolean }>;

  // Helpers
  isConnected: boolean;
  isReconnecting: boolean;
  canSubmitAction: boolean;
}

const DuelContext = createContext<DuelContextValue | null>(null);

const initialDuelState: DuelState = {
  status: "idle",
  duel_id: null,
  player_role: null,
  opponent_player: null,
  your_team: [],
  your_team_name: null,
  opponent_team: [],
  opponent_team_name: null,
  your_available_pokemon: [],
  opponent_available_pokemon: [],
  your_banned_picks: [],
  your_picks: [],
  opponent_banned_picks: [],
  opponent_picks: [],
  current_step: null,
  current_battle: null,
  last_battle: null,
  your_score: 0,
  opponent_score: 0,
  duel_draft_type: null,
  map_id: null,
  error: null,
  connection_status: "disconnected",
};

export const DuelProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [duelState, setDuelState] = useState<DuelState>(initialDuelState);
  const [queueStatus, setQueueStatus] = useState<
    "idle" | "searching" | "matched" | "error"
  >("idle");
  const [queuedTeam, setQueuedTeam] = useState<QueuedTeamPokemon[] | null>(
    null,
  );
  const wsServiceRef = useRef<DuelWebSocketService | null>(null);
  const stepTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const queuePollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isEnqueuingRef = useRef<boolean>(false);
  const hasAutoSubmittedRef = useRef<boolean>(false);

  const [isReconnecting, setIsReconnecting] = useState<boolean>(false);

  /**
   * Handle incoming messages from WebSocket
   */
  const handleMessage = useCallback((message: ServerMessage) => {
    switch (message.type) {
      case "duel_initialized": {
        setQueueStatus("idle");

        console.log(
          "[DuelProvider] duel_initialized status:",
          message.payload.duel_status,
        );

        // Default to waiting_for_step so the UI renders the draft scene.
        // "connected" status is not handled by the main view, leading to empty screen.
        let status: DuelState["status"] = "waiting_for_step";

        if (
          message.payload.duel_status === "FINISHED" ||
          message.payload.duel_status === "COMPLETED"
        ) {
          status = "finished";
        } else if (message.payload.duel_status === "IN_PROGRESS") {
          status = "waiting_for_step";

          // Check if we are in battle phase (steps completed)
          const draftType = getDraftType(message.payload.duel_draft_type);
          if (
            draftType &&
            message.payload.current_step_index >= draftType.steps.length
          ) {
            status = "in_battle";
          }
        }

        console.log("[DuelProvider] duel_initialized payload team names:", {
          your_team_name: message.payload.your_team_name,
          opponent_team_name: message.payload.opponent_team_name,
        });

        const yourPicks = message.payload.your_picks ?? [];
        const oppPicks = message.payload.opponent_picks ?? [];
        const yourBans = message.payload.your_banned_picks ?? [];
        const oppBans = message.payload.opponent_banned_picks ?? [];
        const allUsedIds = [...yourPicks, ...oppPicks, ...yourBans, ...oppBans];

        setDuelState((prev) => ({
          ...prev,
          status,
          duel_id: message.payload.duel_id,
          player_role: message.payload.player_role,
          opponent_player: message.payload.opponent,
          your_team: message.payload.your_team,
          your_team_name: message.payload.your_team_name,
          opponent_team: message.payload.opponent_team,
          opponent_team_name: message.payload.opponent_team_name,
          your_available_pokemon: message.payload.your_team
            .map((p) => p.id)
            .filter((id) => !allUsedIds.includes(id)),
          opponent_available_pokemon: message.payload.opponent_team
            .map((p) => p.id)
            .filter((id) => !allUsedIds.includes(id)),
          your_banned_picks: yourBans,
          your_picks: yourPicks,
          opponent_banned_picks: oppBans,
          opponent_picks: oppPicks,
          duel_draft_type: message.payload.duel_draft_type,
          map_id: message.payload.map_id,
          your_score: message.payload.your_score ?? 0,
          opponent_score: message.payload.opponent_score ?? 0,
          current_round_index: message.payload.current_round_index,
        }));

        // Reset reconnecting flag after state is updated (use setTimeout to let state propagate first)
        setTimeout(() => setIsReconnecting(false), 100);
        break;
      }

      case "duel_round_started": {
        setDuelState((prev) => ({
          ...prev,
          current_round_index: message.payload.round_index,
          // If we're in battle_completed state, this means we should transition back to drafting
          status:
            prev.status === "battle_completed"
              ? "new_round_starting"
              : prev.status,
          // Reset picks/bans for the new round so UI is clean before step_started
          opponent_picks: [],
          your_picks: [],
          your_banned_picks: [],
          opponent_banned_picks: [],
          opponent_available_pokemon: prev.opponent_team.map((p) => p.id),
          your_available_pokemon: prev.your_team.map((p) => p.id),
        }));
        break;
      }

      case "step_started": {
        // Reset auto-submit flag for new step (unless already submitted on reconnect)
        if (!message.payload.already_submitted) {
          hasAutoSubmittedRef.current = false;
        }

        const stepState: DuelStepState = {
          round_index: message.payload.round_index,
          step_index: message.payload.step_index,
          step_type: message.payload.step_type,
          duration_seconds: message.payload.duration_seconds,
          picks_count: message.payload.picks_count,
          available_options: message.payload.available_options,
          started_at: message.payload.started_at,
          your_choices: message.payload.your_picks ?? [],
          opponent_choices: [],
          time_remaining:
            message.payload.time_remaining ?? message.payload.duration_seconds,
          bonus_time_remaining: message.payload.your_remaining_bonus_time,
          your_remaining_bonus_time: message.payload.your_remaining_bonus_time,
        };

        // If player already submitted (e.g., auto-filled while disconnected), go to waiting state
        const newStatus = message.payload.already_submitted
          ? "waiting_for_opponent_pick"
          : "in_step";

        setDuelState((prev) => {
          let updates: Partial<DuelState> = {
            status: newStatus,
            current_step: stepState,
            current_round_index: message.payload.round_index,
          };

          // If round+, reset round-related data
          if (
            prev.current_round_index !== undefined &&
            message.payload.round_index > prev.current_round_index
          ) {
            updates = {
              ...updates,
              opponent_picks: [],
              your_picks: [],
              your_banned_picks: [],
              opponent_banned_picks: [],
              opponent_available_pokemon: prev.opponent_team.map((p) => p.id),
              your_available_pokemon: prev.your_team.map((p) => p.id),
            };
          }

          console.log("step_started updates", updates);

          return {
            ...prev,
            ...updates,
          };
        });

        // Start countdown timer (even if already submitted, to show opponent's remaining time)
        startStepTimer(
          message.payload.time_remaining ?? message.payload.duration_seconds,
          message.payload.your_remaining_bonus_time,
        );
        break;
      }

      case "step_results": {
        setDuelState((prev) => {
          if (!prev.current_step) return prev;

          return {
            ...prev,
            status: "step_completed",
            current_step: {
              ...prev.current_step,
              your_choices: message.payload.your_choices,
              opponent_choices: message.payload.opponent_choices,
            },
            your_available_pokemon: message.payload.your_updated_available,
            opponent_available_pokemon:
              message.payload.opponent_updated_available,
            your_banned_picks: message.payload.your_banned_picks,
            your_picks: message.payload.your_picks,
            opponent_banned_picks: message.payload.opponent_banned_picks,
            opponent_picks: message.payload.opponent_picks,
          };
        });

        stopStepTimer();
        break;
      }

      case "battle_starting": {
        setDuelState((prev) => ({
          ...prev,
          status: "battle_starting",
          current_battle: null,
        }));
        break;
      }

      case "battle_started": {
        const battleState: DuelBattleState = {
          round_index: message.payload.round_index,
          your_team: message.payload.your_team,
          opponent_team: message.payload.opponent_team,
          battle_log_url: message.payload.battle_log_url,
        };

        setDuelState((prev) => ({
          ...prev,
          status: "in_battle",
          current_battle: battleState,
          current_round_index: message.payload.round_index,
          your_score: message.payload.your_score ?? prev.your_score,
          opponent_score: message.payload.opponent_score ?? prev.opponent_score,
        }));

        break;
      }

      case "duel_finished": {
        let finishReason: DuelState["finish_reason"] = "score";
        if (message.payload.quit) {
          finishReason =
            message.payload.quitter === "player_a"
              ? "player_a_quit"
              : "player_b_quit";
        }

        setDuelState((prev) => ({
          ...prev,
          status: "finished",
          your_score: message.payload.final_score_you,
          opponent_score: message.payload.final_score_opponent,
          finish_reason: finishReason,
          result: {
            old_rating: message.payload.rating,
            new_rating: message.payload.new_rating,
            winner: message.payload.winner as "you" | "opponent", // TODO = FIX
            coins_gain: message.payload.coins_gain,
            shards_gain: message.payload.shards_gain,
            rewarded_duels_remaining: message.payload.rewarded_duels_remaining,
          },
        }));
        break;
      }

      case "error": {
        setIsReconnecting(false);
        setDuelState((prev) => ({
          ...prev,
          status: "error",
          error: {
            code: message.payload.code,
            message: message.payload.message,
          },
        }));
        break;
      }

      case "connection_status": {
        console.log("Connection status:", message.payload.status);
        break;
      }

      case "submission_ignored": {
        // Server rejected our submission (time expired) - wait for server to auto-pick
        console.log(
          "[DuelProvider] Submission ignored by server:",
          message.payload.reason,
        );

        // Mark that we shouldn't retry auto-submit
        hasAutoSubmittedRef.current = true;

        // Update status to indicate we're waiting for server
        setDuelState((prev) => ({
          ...prev,
          status: "waiting_for_opponent_pick",
        }));
        break;
      }

      default:
        console.warn("Unknown message type:", (message as any).type);
    }
  }, []);

  /**
   * Handle connection status changes
   */
  const handleConnectionChange = useCallback(
    (status: WebSocketConnectionStatus) => {
      console.log("Connection status changed:", status);
      setDuelState((prev) => ({
        ...prev,
        connection_status:
          status === "connected"
            ? "connected"
            : status === "connecting"
            ? "connected"
            : "disconnected",
      }));

      if (status === "disconnected") {
        stopStepTimer();
      }
    },
    [],
  );

  /**
   * Handle errors
   */
  const handleError = useCallback((error: Error) => {
    console.error("WebSocket error:", error);

    setDuelState((prev) => ({
      ...prev,
      status: "error",
      error: {
        code: "WEBSOCKET_ERROR",
        message: error.message,
      },
    }));
  }, []);

  /**
   * Auto-submit random pick/ban when timer runs out
   */
  const autoSubmitRandomPick = () => {
    if (hasAutoSubmittedRef.current) return;
    hasAutoSubmittedRef.current = true;

    setDuelState((prev) => {
      if (!prev.current_step || !prev.duel_id || !wsServiceRef.current) {
        return prev;
      }

      // Already waiting for opponent means we already submitted
      if (prev.status === "waiting_for_opponent_pick") {
        return prev;
      }

      const { step_type, available_options, picks_count, step_index } =
        prev.current_step;

      // Pick random pokemon from available options
      const shuffled = [...available_options].sort(() => Math.random() - 0.5);
      const randomPicks = shuffled.slice(0, picks_count);

      console.log(
        `[DuelProvider] Auto-submitting random ${step_type}:`,
        randomPicks,
      );

      // Send via websocket
      wsServiceRef.current.sendPlayerAction(
        prev.duel_id,
        step_index,
        step_type,
        randomPicks,
      );

      return {
        ...prev,
        status: "waiting_for_opponent_pick",
      };
    });
  };

  /**
   * Start step countdown timer
   * First drains base time, then bonus time
   */
  const startStepTimer = (durationSeconds: number, bonusTime: number) => {
    stopStepTimer();

    const startTime = Date.now();
    const baseEndTime = startTime + durationSeconds * 1000;

    stepTimerRef.current = setInterval(() => {
      const now = Date.now();
      const elapsedMs = now - startTime;
      const elapsedSeconds = elapsedMs / 1000;

      let baseTimeRemaining = Math.max(
        0,
        Math.floor((baseEndTime - now) / 1000),
      );
      let bonusTimeRemaining = bonusTime;

      // If base time has run out, start draining bonus time
      if (elapsedSeconds > durationSeconds) {
        baseTimeRemaining = 0;
        const bonusElapsed = elapsedSeconds - durationSeconds;
        bonusTimeRemaining = Math.max(0, bonusTime - bonusElapsed);
      }

      setDuelState((prev) => {
        if (!prev.current_step) return prev;

        return {
          ...prev,
          current_step: {
            ...prev.current_step,
            time_remaining: baseTimeRemaining,
            bonus_time_remaining: bonusTimeRemaining,
          },
        };
      });

      // Auto-submit when both times reach 0
      if (baseTimeRemaining === 0 && bonusTimeRemaining === 0) {
        stopStepTimer();
        autoSubmitRandomPick();
      }
    }, 100); // Update every 100ms for smoother countdown
  };

  /**
   * Stop step countdown timer
   */
  const stopStepTimer = () => {
    if (stepTimerRef.current) {
      clearInterval(stepTimerRef.current);
      stepTimerRef.current = null;
    }
  };

  /**
   * Connect to duel WebSocket
   */
  const connectToDuel = useCallback(
    async (
      duelId: string,
      playerId: string,
      token: string,
      websocketUrl: string,
      isReconnecting?: boolean,
    ) => {
      try {
        console.log(
          "[DuelProvider] connectToDuel called, isReconnecting param:",
          isReconnecting,
        );
        if (isReconnecting) {
          console.log("[DuelProvider] Setting isReconnecting to true");
          setIsReconnecting(true);
        }

        setDuelState((prev) => ({ ...prev, status: "connecting" }));

        // Create WebSocket service
        const wsService = new DuelWebSocketService({
          url: websocketUrl,
          duel_id: duelId,
          player_id: playerId,
          token,
        });

        // Register handlers
        wsService.onMessage(handleMessage);
        wsService.onConnectionChange(handleConnectionChange);
        wsService.onError(handleError);

        // Connect
        await wsService.connect();

        wsServiceRef.current = wsService;
        setDuelState((prev) => {
          // Don't overwrite status if message handlers have already updated it
          // (e.g., duel_initialized or step_started arrived before this callback)
          const statusAlreadySet =
            prev.status !== "connecting" && prev.status !== "idle";
          return {
            ...prev,
            status: statusAlreadySet ? prev.status : "initializing",
            duel_id: duelId,
          };
        });

        // Ensure queue status is cleared since we are now connecting/connected
        setQueueStatus("idle");
      } catch (error) {
        console.error("Failed to connect to duel:", error);
        setDuelState((prev) => ({
          ...prev,
          status: "error",
          error: {
            code: "CONNECTION_FAILED",
            message:
              error instanceof Error ? error.message : "Failed to connect",
          },
        }));
      }
    },
    [handleMessage, handleConnectionChange, handleError],
  );

  /**
   * Enqueue player and poll for match
   */
  const enqueue = useCallback(
    async (
      token: string,
      playerId: string,
      queueId: string,
      teamId: number,
      teamPokemons?: QueuedTeamPokemon[],
    ) => {
      try {
        isEnqueuingRef.current = true;
        setQueueStatus("searching");
        setQueuedTeam(teamPokemons ?? null);

        const response = await enqueuePlayer(token, {
          queue_id: queueId,
          team_id: teamId,
        });

        // Check if we were cancelled during the await
        if (!isEnqueuingRef.current) {
          console.log("Enqueue cancelled during API call, aborting.");
          // Attempt to dequeue just in case the API call succeeded but we locally cancelled
          // (Though normally handleCancel calls dequeuePlayer too)
          return;
        }

        if (!response.ok) {
          console.error("Failed to enqueue:", response.message);
          setQueueStatus("error");
          isEnqueuingRef.current = false;
          return;
        }

        // Start polling
        if (queuePollingRef.current) clearInterval(queuePollingRef.current);

        queuePollingRef.current = setInterval(async () => {
          // Check cancellation again inside interval just to be safe
          if (!isEnqueuingRef.current) {
            if (queuePollingRef.current) clearInterval(queuePollingRef.current);
            return;
          }

          try {
            const statusResponse = await getPlayerQueueStatus(token);
            if (statusResponse.ok && statusResponse.data) {
              if (
                statusResponse.data.status === "MATCHED" &&
                statusResponse.data.queue_entry?.duel_id
              ) {
                const duelId = statusResponse.data.queue_entry.duel_id;
                setQueueStatus("matched");
                isEnqueuingRef.current = false; // Stop enqueuing logic
                if (queuePollingRef.current) {
                  clearInterval(queuePollingRef.current);
                  queuePollingRef.current = null;
                }

                // Connect to duel
                const wsUrl = import.meta.env.VITE_WS_API_URL;
                if (wsUrl) {
                  await connectToDuel(duelId, playerId, token, wsUrl);
                } else {
                  console.error("Missing VITE_WS_API_URL");
                  setQueueStatus("error");
                }
              }
            }
          } catch (e) {
            console.error("Polling error:", e);
          }
        }, 3000);
      } catch (e) {
        console.error("Enqueue error:", e);
        setQueueStatus("error");
        isEnqueuingRef.current = false;
      }
    },
    [connectToDuel],
  );

  /**
   * Cancel queue
   */
  const cancelQueue = useCallback(async (token: string) => {
    isEnqueuingRef.current = false; // Signal to abort any pending operations

    if (queuePollingRef.current) {
      clearInterval(queuePollingRef.current);
      queuePollingRef.current = null;
    }

    try {
      await dequeuePlayer(token);
    } catch (e) {
      console.error("Failed to dequeue:", e);
      // We still set status to idle locally so UI updates, but warn
    }

    setQueueStatus("idle");
    setQueuedTeam(null);
  }, []);

  /**
   * Create a new duel and connect to it
   */
  const createAndConnectToDuel = useCallback(
    async (
      token: string,
      playerId: string,
      opponentPlayerId: string,
      draftType: string,
    ): Promise<{ success: boolean; error?: string }> => {
      try {
        setDuelState((prev) => ({ ...prev, status: "connecting" }));

        // Create duel via HTTP
        const response = await createDuel(token, {
          opponent_player_id: opponentPlayerId,
          duel_draft_type: draftType,
          is_bot_match: true,
        });

        if (
          !response.ok ||
          !response.data?.duel_id ||
          !response.data?.websocket_url
        ) {
          const errorMessage = response.message || "Failed to create duel";
          setDuelState((prev) => ({
            ...prev,
            status: "error",
            error: {
              code: "DUEL_CREATION_FAILED",
              message: errorMessage,
            },
          }));
          return { success: false, error: errorMessage };
        }

        console.log("Duel created:", response.data.duel_id);

        // Get WebSocket URL from env or response
        const wsUrl =
          import.meta.env.VITE_WS_API_URL || response.data.websocket_url;

        // Connect to WebSocket
        await connectToDuel(response.data.duel_id, playerId, token, wsUrl);

        return { success: true };
      } catch (error) {
        console.error("Failed to create and connect to duel:", error);
        const errorMessage =
          error instanceof Error ? error.message : "Failed to initialize duel";
        setDuelState((prev) => ({
          ...prev,
          status: "error",
          error: {
            code: "INITIALIZATION_FAILED",
            message: errorMessage,
          },
        }));
        return { success: false, error: errorMessage };
      }
    },
    [connectToDuel],
  );

  /**
   * Disconnect from duel
   */
  const disconnectFromDuel = useCallback(() => {
    console.log(
      "[DuelProvider] disconnectFromDuel called - resetting state to idle",
    );

    if (wsServiceRef.current) {
      wsServiceRef.current.destroy();
      wsServiceRef.current = null;
    }

    // Ensure queue polling is stopped
    isEnqueuingRef.current = false;
    if (queuePollingRef.current) {
      clearInterval(queuePollingRef.current);
      queuePollingRef.current = null;
    }

    stopStepTimer();
    setDuelState(initialDuelState);
    setQueueStatus("idle");
    setQueuedTeam(null);
    setIsReconnecting(false);
  }, []);

  /**
   * Quit current duel
   */
  const quitDuel = useCallback(
    async (token: string, _duelId?: string) => {
      const duelId = _duelId ?? duelState.duel_id;

      try {
        // If no duelId is provided, still permit quit as a panic quit for queue-stuck cases
        const result = await quitDuelService(token, duelId ?? "no-duel-id");

        if (result.ok && result.data?.success) {
          // Check if this was a zombie duel cleanup (duel no longer existed)
          // In this case, there's no WebSocket message coming, so clean up immediately
          const isZombieCleanup =
            result.data.message?.includes("no longer exists");
          if (isZombieCleanup) {
            disconnectFromDuel();
          }
          return { success: true, zombieCleanup: isZombieCleanup };
        } else {
          return {
            success: false,
            error: result.message || "Failed to quit duel",
          };
        }
      } catch (error) {
        console.error("Failed to quit duel:", error);
        return {
          success: false,
          error: error instanceof Error ? error.message : "Failed to quit duel",
        };
      }
    },
    [duelState.duel_id, disconnectFromDuel],
  );

  /**
   * Submit pick action
   */
  const submitPick = useCallback(
    (pokemonIds: number[]) => {
      if (
        !wsServiceRef.current ||
        !duelState.duel_id ||
        !duelState.current_step
      ) {
        console.error("Cannot submit pick: not in valid state");
        return;
      }

      wsServiceRef.current.sendPlayerAction(
        duelState.duel_id,
        duelState.current_step.step_index,
        "pick",
        pokemonIds,
      );

      setDuelState((prev) => ({
        ...prev,
        status: "waiting_for_opponent_pick",
      }));
    },
    [duelState.duel_id, duelState.current_step],
  );

  /**
   * Submit ban action
   */
  const submitBan = useCallback(
    (pokemonIds: number[]) => {
      if (
        !wsServiceRef.current ||
        !duelState.duel_id ||
        !duelState.current_step
      ) {
        console.error("Cannot submit ban: not in valid state");
        return;
      }

      wsServiceRef.current.sendPlayerAction(
        duelState.duel_id,
        duelState.current_step.step_index,
        "ban",
        pokemonIds,
      );

      setDuelState((prev) => ({
        ...prev,
        status: "waiting_for_opponent_pick",
      }));
    },
    [duelState.duel_id, duelState.current_step],
  );

  /**
   * Acknowledge battle completion
   */
  const acknowledgeBattleEnd = useCallback(() => {
    const roundIndex =
      duelState.current_battle?.round_index ?? duelState.current_round_index;

    if (
      !wsServiceRef.current ||
      !duelState.duel_id ||
      roundIndex === undefined
    ) {
      console.error("Cannot acknowledge battle: not in valid state");
      return;
    }

    wsServiceRef.current.sendVisualAck(duelState.duel_id, roundIndex);

    setDuelState((prev) => {
      // Check if we already received a newer round while watching the replay
      const currentRound = prev.current_round_index ?? -1;
      const battleRound = prev.current_battle?.round_index ?? -1;

      // If round index has advanced, we should jump straight to new_round_starting
      const nextStatus =
        currentRound > battleRound ? "new_round_starting" : "battle_completed";

      return {
        ...prev,
        status: nextStatus,
        last_battle: prev.current_battle,
        current_battle: null,
      };
    });
  }, [
    duelState.duel_id,
    duelState.current_battle,
    duelState.current_round_index,
  ]);

  /**
   * Cleanup on unmount
   */
  useEffect(() => {
    return () => {
      if (wsServiceRef.current) {
        wsServiceRef.current.destroy();
      }
      stopStepTimer();
      if (queuePollingRef.current) {
        clearInterval(queuePollingRef.current);
      }
    };
  }, []);

  const value = useMemo<DuelContextValue>(
    () => ({
      duelState,
      setDuelState,
      queueStatus,
      queuedTeam,
      createAndConnectToDuel,
      enqueue,
      cancelQueue,
      connectToDuel,
      disconnectFromDuel,
      submitPick,
      submitBan,
      acknowledgeBattleEnd,
      quitDuel,
      isConnected: duelState.connection_status === "connected",
      isReconnecting,
      canSubmitAction:
        duelState.status === "in_step" && duelState.current_step !== null,
    }),
    [
      duelState,
      setDuelState,
      queueStatus,
      queuedTeam,
      createAndConnectToDuel,
      enqueue,
      cancelQueue,
      connectToDuel,
      disconnectFromDuel,
      submitPick,
      submitBan,
      acknowledgeBattleEnd,
      quitDuel,
      isReconnecting,
    ],
  );

  return <DuelContext.Provider value={value}>{children}</DuelContext.Provider>;
};

/**
 * Hook to access duel context
 */
export function useDuel() {
  const ctx = useContext(DuelContext);
  if (!ctx) throw new Error("useDuel must be used within <DuelProvider>");
  return ctx;
}

/**
 * Hook to access duel connection state
 */
export function useDuelConnection() {
  const { isConnected, duelState } = useDuel();
  return {
    isConnected,
    connectionStatus: duelState.connection_status,
    status: duelState.status,
  };
}
